import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// import ProService from '../../services/ProService';

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [booking, setBooking] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false); // eslint-disable-line no-unused-vars

  useEffect(() => {
    fetchBookingPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchBookingPage = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://klorah-fast-server-my8l.onrender.com/api/booking/page/${slug}`);
      if (!response.ok) throw new Error('Booking page not found');
      const data = await response.json();
      setPageData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async (date) => {
    if (!selectedService || !pageData) return;
    try {
      setLoadingSlots(true);
      const response = await fetch(
        `https://klorah-fast-server-my8l.onrender.com/api/booking/availability/${pageData.userId}?service_id=${selectedService.id}&date=${date}`
      );
      const data = await response.json();
      setAvailableSlots(data.slots || []);
    } catch (err) {
      console.error('Failed to fetch availability:', err);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    fetchAvailability(date);
  };

  const handleSubmit = async () => {
    try {
      setBooking(true);
      const response = await fetch('https://klorah-fast-server-my8l.onrender.com/api/booking/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: pageData.userId,
          serviceId: selectedService.id,
          date: selectedDate,
          time: selectedTime,
          clientName: formData.name,
          clientEmail: formData.email,
          clientPhone: formData.phone,
          notes: formData.notes,
        }),
      });

      if (!response.ok) throw new Error('Failed to create booking');
      setBookingComplete(true);
      setStep(4);
    } catch (err) {
      alert('Failed to create booking. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  const getDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' }),
      });
    }
    return dates;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Page Not Found</h1>
          <p className="text-gray-500 dark:text-[#666666]">This booking page doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-[#1A1A1A]">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4">
            {pageData.profileImage && (
              <img
                src={pageData.profileImage}
                alt={pageData.businessName}
                className="w-16 h-16 rounded-full object-cover"
              />
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{pageData.title}</h1>
              {pageData.businessName && (
                <p className="text-gray-500 dark:text-[#666666]">{pageData.businessName}</p>
              )}
            </div>
          </div>
          {pageData.description && (
            <p className="text-gray-600 dark:text-[#999999] mt-4">{pageData.description}</p>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          {['Service', 'Date & Time', 'Details', 'Confirm'].map((label, index) => (
            <div key={label} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step > index + 1
                    ? 'bg-brand text-white'
                    : step === index + 1
                    ? 'bg-brand text-white'
                    : 'bg-gray-200 dark:bg-[#1A1A1A] text-gray-500 dark:text-[#666666]'
                }`}
              >
                {step > index + 1 ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  index + 1
                )}
              </div>
              {index < 3 && (
                <div className={`w-16 sm:w-24 h-0.5 mx-2 ${step > index + 1 ? 'bg-brand' : 'bg-gray-200 dark:bg-[#1A1A1A]'}`} />
              )}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Select Service */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select a Service</h2>
              <div className="space-y-3">
                {pageData.services?.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => {
                      setSelectedService(service);
                      setStep(2);
                    }}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      selectedService?.id === service.id
                        ? 'border-brand bg-brand/10'
                        : 'border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-[#2A2A2A] bg-white dark:bg-[#111111]'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{service.name}</p>
                        {service.description && (
                          <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">{service.description}</p>
                        )}
                        <p className="text-sm text-gray-400 dark:text-[#666666] mt-2">{service.duration} minutes</p>
                      </div>
                      <p className="text-lg font-semibold text-brand">${service.price}</p>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Select Date & Time */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setStep(1)}
                className="text-sm text-gray-500 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white mb-4 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Date & Time</h2>

              {/* Date Picker */}
              <div className="mb-6">
                <p className="text-sm text-gray-500 dark:text-[#666666] mb-3">Select a date</p>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
                  {getDates().map((d) => (
                    <button
                      key={d.date}
                      onClick={() => handleDateSelect(d.date)}
                      className={`flex-shrink-0 w-16 py-3 rounded-xl text-center transition-all ${
                        selectedDate === d.date
                          ? 'bg-brand text-white'
                          : 'bg-gray-100 dark:bg-[#111111] text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-[#1A1A1A]'
                      }`}
                    >
                      <p className="text-xs opacity-70">{d.day}</p>
                      <p className="text-lg font-semibold">{d.dayNum}</p>
                      <p className="text-xs opacity-70">{d.month}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <p className="text-sm text-gray-500 dark:text-[#666666] mb-3">Available times</p>
                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-gray-500 dark:text-[#666666] text-center py-8">No available times on this date</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => {
                            setSelectedTime(slot.time);
                            setStep(3);
                          }}
                          className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                            selectedTime === slot.time
                              ? 'bg-brand text-white'
                              : 'bg-gray-100 dark:bg-[#111111] text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-[#1A1A1A]'
                          }`}
                        >
                          {slot.display}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Details */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setStep(2)}
                className="text-sm text-gray-500 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white mb-4 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Your Details</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#1A1A1A] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand resize-none"
                    rows={3}
                    placeholder="Anything else we should know?"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="mt-6 p-4 bg-gray-50 dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A]">
                <p className="text-sm text-gray-500 dark:text-[#666666] mb-2">Booking Summary</p>
                <div className="space-y-1">
                  <p className="font-medium text-gray-900 dark:text-white">{selectedService?.name}</p>
                  <p className="text-gray-500 dark:text-[#666666] text-sm">
                    {new Date(selectedDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })} at {selectedTime}
                  </p>
                  <p className="text-brand font-semibold mt-2">${selectedService?.price}</p>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!formData.name || !formData.email || booking}
                className="w-full mt-4 py-4 bg-brand text-white font-semibold rounded-xl hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {booking ? 'Booking...' : 'Confirm Booking'}
              </button>
            </motion.div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Booking Confirmed!</h2>
              <p className="text-gray-500 dark:text-[#666666] mb-6">
                We've sent a confirmation email to {formData.email}
              </p>

              <div className="p-4 bg-gray-50 dark:bg-[#111111] rounded-xl text-left mb-6 border border-gray-200 dark:border-[#1A1A1A]">
                <p className="text-sm text-gray-500 dark:text-[#666666] mb-2">Your appointment</p>
                <p className="font-medium text-gray-900 dark:text-white">{selectedService?.name}</p>
                <p className="text-gray-500 dark:text-[#666666]">
                  {new Date(selectedDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })} at {selectedTime}
                </p>
              </div>

              <p className="text-sm text-gray-400 dark:text-[#666666]">
                Need to make changes? Contact us directly.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
